import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import MenuItem from '@mui/material/MenuItem';
import { SxProps } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import Typography from '@mui/material/Typography';
import { DatePicker } from '@mui/x-date-pickers-pro';
import { NameIdObj } from 'common/models/GenericTypes';
import MultipleGroupPicker from 'components/Pickers/MultipleGroupPicker';
import MultipleNumericValuesPicker from 'components/Pickers/MultipleNumericValuesPicker';
import MultipleUserPicker from 'components/Pickers/MultipleUserPicker';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import { GetAllTagsSettings } from 'services/TagsService';
import { GetCustomerSettingBySettingName } from 'services/ConfigurationService';
import { GetDatesFromTimeRangeFilter } from 'util/DateUtils';
import { TaggingReportFilter } from 'common/models/Reporting/AnalyticsFilterData';
import { GetTaggingReportSummaryData } from 'services/DashboardService';
import { TaggingReportRecord } from 'common/models/Reports/TaggingReportRecord';
import GridWithStateWrapper from 'components/GridWidthStateWrapper';
import {
  GridColDef,
  GridRenderCellParams,
  GridValueGetter,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import moment from 'moment';
import { PreviewEntityType } from 'common/models/Previews/Previews';
import { Link } from 'react-router-dom';
import PreviewLoaderComponent from 'components/Previews/PreviewLoader';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import ClientsGridComponent from 'components/Grids/ClientsGrid';
import ContactsGridComponent from 'components/Grids/ContactsGrid';
import CandidatesGridComponent from 'components/Grids/CandidatesGrid';
import JobsGridComponent from 'components/Grids/JobsGrid';
import PlacementsGridComponent from 'components/Grids/PlacementsGrid';
import OpportunitiesGridComponent from 'components/Grids/OpportunitiesGrid';
import SitesGridComponent from 'components/Grids/SitesGrid';

interface Props {
  description: string;
  loadingHandler?: (isLoading: boolean) => void;
  errorHandler?: (message: string) => void;
  successHandler?: (message: string) => void;
}

interface TagCategory {
  id: string;
  name: string;
  entityId: number;
  typeId: number;
}

type CategoryMap = Record<number, TagCategory[]>;

const linkStyle: React.CSSProperties = { color: 'inherit', textDecoration: 'underline' };
const filterElementStyle: SxProps = { flex: '1 1 0' };
const defaultTimeRange = 2;

const moduleOptions: NameIdObj[] = [
  { id: 0, name: 'All' },
  { id: 1, name: 'Clients' },
  { id: 2, name: 'Contacts' },
  { id: 3, name: 'Candidates' },
  { id: 4, name: 'Jobs' },
  { id: 5, name: 'Placements' },
  { id: 8, name: 'Opportunities' },
  { id: 9, name: 'Sites' },
];

const moduleNameMap: Record<number, string> = {
  0: 'All',
  1: 'Client',
  2: 'Contact',
  3: 'Candidate',
  4: 'Job',
  5: 'Placement',
  8: 'Opportunity',
  9: 'Site',
};

const quickViewEntityMap: Record<number, PreviewEntityType> = {
  1: 'client',
  2: 'contact',
  3: 'candidate',
  4: 'job',
  5: 'placement',
  8: 'opportunity',
  9: 'site',
};

const moduleUrlPathMap: Record<number, string> = {
  1: 'clients',
  2: 'contacts',
  3: 'candidates',
  4: 'jobs',
  5: 'placements',
  8: 'opportunities',
  9: 'sites',
};

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;
const allCategoryOption: TagCategory = {
  entityId: 0,
  id: '0',
  name: 'All',
  typeId: 0,
};

export default function RecordTaggingReport({
  description,
  loadingHandler,
  errorHandler,
  successHandler,
}: Props) {
  const [isInitialSetupFinished, setIsInitialSetupFinished] = useState(false);
  const [financialYearStartMonth, setFinancialYearStartMonth] = useState(1);
  const [showGrid, setShowGrid] = useState(false);
  const [sourceType, setSourceType] = useState(1);
  const [selectedModules, setSelectedModules] = useState<number[]>([0]);
  const [selectedGroups, setSelectedGroups] = useState<number[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [timeRange, setTimeRange] = useState(defaultTimeRange);
  const [startDate, setStartDate] = useState<moment.Moment | null>(null);
  const [endDate, setEndDate] = useState<moment.Moment | null>(null);
  const [categoriesByModuleMap, setCategoriesByModuleMap] = useState<CategoryMap>({});
  const [selectedCategories, setSelectedCategories] = useState<TagCategory[]>([allCategoryOption]);
  const [rows, setRows] = useState<TaggingReportRecord[]>([]);
  const [filterData, setFilterData] = useState<TaggingReportFilter>();

  const [activeTab, setActiveTab] = useState('summary');
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [showTagsPrevewNoDelay, setShowTagsPreviewNoDelay] = useState(false);
  const [previewType, setPreviewType] = useState<PreviewEntityType | ''>('');
  const [previewRecordId, setPreviewRecordId] = useState(0);
  const [isPreviewTags, setIsPreviewTags] = useState(false);
  const [previewRecordName, setPreviewRecordName] = useState('');
  const apiRef = useGridApiRef();

  useEffect(() => {
    const getSetupData = async () => {
      loadingHandler?.(true);
      const [tagSettings, yearStart] = await Promise.all([
        GetAllTagsSettings(errorHandler),
        GetCustomerSettingBySettingName('YearStart'),
      ]);

      if (yearStart) {
        const parsed = +yearStart;
        if (parsed >= 1 && parsed <= 12) setFinancialYearStartMonth(parsed);
      }

      if (tagSettings) {
        const activeOnly = tagSettings.filter((s) => s.visible);
        const categoriesMap: CategoryMap = {};
        for (let i = 0; i < activeOnly.length; i++) {
          const c = activeOnly[i];
          const mapArray = categoriesMap[c.entityId];

          const category: TagCategory = {
            id: `${c.entityId}-${c.tagTypeId}`,
            entityId: c.entityId,
            name: c.tagAgencyName,
            typeId: c.tagTypeId,
          };

          if (mapArray) mapArray.push(category);
          else categoriesMap[c.entityId] = [category];
        }
        setCategoriesByModuleMap(categoriesMap);
      }
      loadingHandler?.(false);
      setIsInitialSetupFinished(true);
    };
    getSetupData();
  }, [loadingHandler, errorHandler]);

  const setDatesByTimeRange = useCallback(
    (tr: number) => {
      const dates = GetDatesFromTimeRangeFilter(tr, financialYearStartMonth);
      if (dates) {
        const [start, end] = dates;
        setStartDate(start);
        setEndDate(end);
      }
    },
    [financialYearStartMonth]
  );

  useEffect(() => {
    if (isInitialSetupFinished) {
      setDatesByTimeRange(timeRange);
    }
  }, [timeRange, isInitialSetupFinished, setDatesByTimeRange]);

  const runReportCallback = useCallback(async () => {
    setActiveTab('summary');
    let sIds: number[] = [];
    if (sourceType === 3) sIds = selectedGroups;
    if (sourceType === 4) sIds = selectedUsers;
    const tagTypes = selectedCategories.map((c) => c.id);

    const f: TaggingReportFilter = {
      sourceType: sourceType,
      sourceIds: sIds,
      moduleIds: selectedModules.includes(0) ? [] : selectedModules,
      tagTypes: tagTypes.includes('0') ? [] : tagTypes,
      startDate: startDate?.isValid() ? startDate.format('YYYY-MM-DD') : '',
      endDate: endDate?.isValid() ? endDate.format('YYYY-MM-DD') : '',
    };

    loadingHandler?.(true);
    const res = await GetTaggingReportSummaryData(f, errorHandler);
    if (res) {
      setRows(res);
      setShowGrid(true);
      setFilterData(f);
    }
    loadingHandler?.(false);
  }, [
    sourceType,
    selectedModules,
    selectedGroups,
    selectedUsers,
    selectedCategories,
    startDate,
    endDate,
    loadingHandler,
    errorHandler,
  ]);

  const columns = useMemo<GridColDef[]>(() => {
    const handlePreviewHover = (
      type: PreviewEntityType | '',
      id: number,
      isTags = false,
      recordName = '',
      noDelay = false
    ) => {
      setPreviewType(type);
      setPreviewRecordId(id);
      setIsPreviewTags(isTags);
      setPreviewRecordName(recordName);
      setIsPreviewOpen(true);
      setShowTagsPreviewNoDelay(noDelay);
    };

    const handlePreviewClose = () => {
      setIsPreviewOpen(false);
      setShowTagsPreviewNoDelay(false);
    };

    const dateValueGetter: GridValueGetter<TaggingReportRecord, any, undefined, string> = (
      value
    ) => {
      if (value) {
        const m = moment(value);
        if (m.isValid() && m.get('year') > 1) {
          return m.toDate();
        }
      }
    };

    const entityTypeNameGetter: GridValueGetter<TaggingReportRecord, any, undefined, number> = (
      value
    ) => {
      if (value) return moduleNameMap[value] ?? '';
    };

    const dateRenderer = (params: GridRenderCellParams) => {
      if (params.value) {
        return moment(params.value).format('DD MMM YYYY');
      }
      if (params.field === 'lastActivityDate') return 'Never';
    };

    const linkToRecordRenderer = (params: GridRenderCellParams) => {
      const quickViewType = quickViewEntityMap[params.row.entityID];
      const urlPath = moduleUrlPathMap[params.row.entityID];
      if (quickViewType && urlPath) {
        const recordId = params.row.playerID;
        return (
          <Link
            to={`/${urlPath}/${recordId}`}
            style={linkStyle}
            onMouseEnter={() => handlePreviewHover(quickViewType, recordId)}
            onMouseLeave={handlePreviewClose}
          >
            {params.value}
          </Link>
        );
      }
      return params.value;
    };

    return [
      {
        field: 'recordName',
        headerName: 'Record Name',
        renderCell: linkToRecordRenderer,
        width: 250,
      },
      { field: 'entityID', headerName: 'Record Type', valueGetter: entityTypeNameGetter },
      { field: 'tagTypeName', headerName: 'Tag Type', width: 250 },
      { field: 'tag', headerName: 'Tag', width: 200 },
      { field: 'recruiterName', headerName: 'Tagged By', width: 250 },
      {
        field: 'taggedDate',
        headerName: 'Tagged Date',
        type: 'date',
        valueGetter: dateValueGetter,
        renderCell: dateRenderer,
        width: 200,
      },
    ];
  }, []);

  const modulesChangeHandler = useCallback((groups: NameIdObj[]) => {
    setSelectedModules(groups.map((u) => u.id));
    setSelectedCategories([allCategoryOption]);
    setActiveTab('summary');
  }, []);

  const groupsChangeHandler = useCallback((groups: NameIdObj[]) => {
    setSelectedGroups(groups.map((u) => u.id));
  }, []);

  const usersChangeHandler = useCallback((users: NameIdObj[]) => {
    setSelectedUsers(users.map((u) => u.id));
  }, []);

  const onTimeRangeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const v = +e.target.value;
    setTimeRange(v);
  }, []);

  const categoryOptions = useMemo<TagCategory[]>(() => {
    const categories: TagCategory[] = [allCategoryOption];
    if (selectedModules.includes(0)) {
      const keys = Object.keys(categoriesByModuleMap);
      for (let i = 0; i < keys.length; i++) {
        const key = keys[i];
        const cats = categoriesByModuleMap[+key];
        if (cats) categories.push(...cats);
      }
    } else if (selectedModules.length > 0) {
      for (let i = 0; i < selectedModules.length; i++) {
        const key = selectedModules[i];
        const cats = categoriesByModuleMap[key];
        if (cats) categories.push(...cats);
      }
    }
    return categories;
  }, [selectedModules, categoriesByModuleMap]);

  const exportAsExcelHandler = useCallback(() => {
    const api = apiRef.current;
    if (api) {
      const m = moment();
      const filename = `TaggingReportSummary${m.format('YYYYMMDDhhmmss')}`;
      api.exportDataAsExcel({ fileName: filename });
    }
  }, [apiRef]);

  const handleTabChange = useCallback((e: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
  }, []);

  const allModulesOptionSelected = useMemo(() => selectedModules.includes(0), [selectedModules]);

  return (
    <>
      <PreviewLoaderComponent
        open={isPreviewOpen}
        entityType={previewType}
        recordId={previewRecordId}
        isTagsPreview={isPreviewTags}
        showDelayMs={showTagsPrevewNoDelay ? 0 : undefined}
        titleOverride={previewRecordName}
        kioskToken={null}
      />
      <Box display="flex" gap={1} mb={1}>
        <TextField
          select
          label="Source Type"
          value={sourceType.toString()}
          onChange={({ target }) => setSourceType(+target.value)}
          sx={filterElementStyle}
        >
          <MenuItem value="1">All</MenuItem>
          <MenuItem value="3">Group</MenuItem>
          <MenuItem value="4">User</MenuItem>
        </TextField>
        <MultipleNumericValuesPicker
          label="Module"
          options={moduleOptions}
          value={selectedModules}
          onSelect={modulesChangeHandler}
          sx={filterElementStyle}
        />
        <Autocomplete
          value={selectedCategories}
          multiple
          size="small"
          disableCloseOnSelect
          options={categoryOptions}
          getOptionLabel={(o) => o.name}
          onChange={(e, v) => setSelectedCategories(v)}
          sx={filterElementStyle}
          renderOption={(props, option, { selected }) => (
            <ListItem {...props} key={option.id}>
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                style={{ marginRight: 8 }}
                checked={selected}
              />
              <ListItemText
                primary={option.name}
                secondary={moduleNameMap[option.entityId] ?? ''}
              />
            </ListItem>
          )}
          renderInput={(params) => <TextField {...params} label="Tag Types" />}
        />
        <Button variant="contained" color="success" onClick={runReportCallback}>
          Run Report
        </Button>
      </Box>
      <Box display={sourceType === 3 ? undefined : 'none'} mb={1}>
        <MultipleGroupPicker value={selectedGroups} onSelect={groupsChangeHandler} />
      </Box>
      <Box display={sourceType === 4 ? undefined : 'none'} mb={1}>
        <MultipleUserPicker
          includeInactiveUsers
          value={selectedUsers}
          onSelect={usersChangeHandler}
        />
      </Box>
      <Box display="flex" gap={1}>
        <TextField
          select
          label="Time Range"
          value={timeRange.toString()}
          onChange={onTimeRangeChange}
          sx={filterElementStyle}
        >
          <MenuItem value="0">Custom Range</MenuItem>
          <MenuItem value="1">This Week</MenuItem>
          <MenuItem value="2">This Month</MenuItem>
          <MenuItem value="3">This Quarter (Calendar)</MenuItem>
          <MenuItem value="13">This Quarter (Financial)</MenuItem>
          <MenuItem value="4">This Year (Calendar)</MenuItem>
          <MenuItem value="14">This Year (Financial)</MenuItem>
          <MenuItem value="5">Last Week</MenuItem>
          <MenuItem value="6">Last Month</MenuItem>
          <MenuItem value="7">Last Quarter (Calendar)</MenuItem>
          <MenuItem value="15">Last Quarter (Financial)</MenuItem>
          <MenuItem value="8">Last Year (Calendar)</MenuItem>
          <MenuItem value="16">Last Year (Financial)</MenuItem>
          <MenuItem value="9">Last 7 Days</MenuItem>
          <MenuItem value="10">Last 30 Days</MenuItem>
          <MenuItem value="11">Last 90 Days</MenuItem>
          <MenuItem value="12">Last 365 Days</MenuItem>
        </TextField>
        <DatePicker
          label="From Date"
          value={startDate}
          onChange={setStartDate}
          disabled={timeRange !== 0}
          sx={filterElementStyle}
          slotProps={{ actionBar: { actions: ['clear', 'today', 'cancel', 'accept'] } }}
        />
        <DatePicker
          label="To Date"
          value={endDate}
          onChange={setEndDate}
          disabled={timeRange !== 0}
          sx={filterElementStyle}
          slotProps={{ actionBar: { actions: ['clear', 'today', 'cancel', 'accept'] } }}
        />
      </Box>
      <Typography component="div" variant="h6" mt={2}>
        Report Description
      </Typography>
      <Typography component="div" variant="body2">
        {description}
      </Typography>
      {showGrid && (
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab value="summary" label="Summary" />
          {(allModulesOptionSelected || selectedModules.includes(1)) && (
            <Tab value="clients" label="Clients" />
          )}
          {(allModulesOptionSelected || selectedModules.includes(2)) && (
            <Tab value="contacts" label="Contacts" />
          )}
          {(allModulesOptionSelected || selectedModules.includes(3)) && (
            <Tab value="candidates" label="Candidates" />
          )}
          {(allModulesOptionSelected || selectedModules.includes(4)) && (
            <Tab value="jobs" label="Jobs" />
          )}
          {(allModulesOptionSelected || selectedModules.includes(5)) && (
            <Tab value="placements" label="Placements" />
          )}
          {(allModulesOptionSelected || selectedModules.includes(8)) && (
            <Tab value="opportunities" label="Opportunities" />
          )}
          {(allModulesOptionSelected || selectedModules.includes(9)) && (
            <Tab value="sites" label="Sites" />
          )}
        </Tabs>
      )}
      {showGrid && activeTab === 'summary' && (
        <>
          <Box pb="10px" ml="auto">
            <Button variant="contained" color="success" onClick={exportAsExcelHandler}>
              Export As Excel
            </Button>
          </Box>
          <GridWithStateWrapper
            gridName="tagging-report-summary"
            rows={rows}
            columns={columns}
            apiRef={apiRef}
          />
        </>
      )}
      {showGrid && filterData && activeTab === 'clients' && (
        <ClientsGridComponent
          gridName="tagging-report-clients"
          source="tagging-report"
          loadingHandler={loadingHandler}
          errorHandler={errorHandler}
          successHandler={successHandler}
          filterData={filterData}
        />
      )}
      {showGrid && filterData && activeTab === 'contacts' && (
        <ContactsGridComponent
          gridName="tagging-report-contacts"
          source="tagging-report"
          loadingHandler={loadingHandler}
          errorHandler={errorHandler}
          successHandler={successHandler}
          filterData={filterData}
        />
      )}
      {showGrid && filterData && activeTab === 'candidates' && (
        <CandidatesGridComponent
          gridName="tagging-report-candidates"
          source="tagging-report"
          loadingHandler={loadingHandler}
          errorHandler={errorHandler}
          successHandler={successHandler}
          filterData={filterData}
        />
      )}
      {showGrid && filterData && activeTab === 'jobs' && (
        <JobsGridComponent
          gridName="tagging-report-jobs"
          source="tagging-report"
          loadingHandler={loadingHandler}
          errorHandler={errorHandler}
          filterData={filterData}
          jobState={0}
        />
      )}
      {showGrid && filterData && activeTab === 'placements' && (
        <PlacementsGridComponent
          gridName="tagging-report-placements"
          source="tagging-report"
          loadingHandler={loadingHandler}
          errorHandler={errorHandler}
          successHandler={successHandler}
          filterData={filterData}
        />
      )}
      {showGrid && filterData && activeTab === 'opportunities' && (
        <OpportunitiesGridComponent
          gridName="tagging-report-opportunities"
          source="tagging-report"
          loadingHandler={loadingHandler}
          errorHandler={errorHandler}
          successHandler={successHandler}
          filterData={filterData}
        />
      )}
      {showGrid && filterData && activeTab === 'sites' && (
        <SitesGridComponent
          gridName="tagging-report-sites"
          source="tagging-report"
          loadingHandler={loadingHandler}
          errorHandler={errorHandler}
          successHandler={successHandler}
          filterData={filterData}
          sourceId={0}
        />
      )}
    </>
  );
}
